import { useNavigate } from 'react-router-dom'
import { ChevronRight } from 'lucide-react'
import { Button } from '@/shared/components/ui/button'
import { routes } from '@/shared/constants'
import { safeFormatDate } from '@/shared/utils/formatDate'
import { formatAmount } from '@/shared/utils/formatAmount'
import { BillingStatusBadge } from './BillingStatusBadge'
import { PAYABLE_STATUSES } from '../constants/billingStatusFilters'
import type { BillingListItem } from '../hooks/useMyBillings'

interface BillingTableProps {
  billings: BillingListItem[]
  onPay: (billingId: number) => void
}

/** Monto total de la factura (el `totalPrices` de su moneda, o el primero). */
function billingAmount(b: BillingListItem): string {
  const totals = b.totalPrices ?? []
  if (totals.length === 0) return '—'
  const primary = totals.find((t) => t.currencyId === b.currencyId) ?? totals[0]
  const map = new Map<number, string>()
  if (b.currency) map.set(b.currency.id, b.currency.name)
  return formatAmount(primary.amount, primary.currencyId ?? b.currencyId, map)
}

/**
 * Tabla de facturas: nº, monto, estado y fecha de emisión. Fila clickable →
 * detalle. Botón "Pagar" en facturas pendientes.
 */
export function BillingTable({ billings, onPay }: BillingTableProps) {
  const navigate = useNavigate()

  return (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-line text-left text-xs font-semibold uppercase tracking-wide text-ink-subtle">
            <th className="px-2 py-3">Factura</th>
            <th className="px-2 py-3">Monto</th>
            <th className="px-2 py-3">Estado</th>
            <th className="px-2 py-3">Emitida</th>
            <th className="px-2 py-3" />
          </tr>
        </thead>
        <tbody>
          {billings.map((b) => {
            const payable = PAYABLE_STATUSES.has(b.status?.toUpperCase() ?? '')
            return (
              <tr
                key={b.id}
                onClick={() => navigate(routes.app.billing.detail(String(b.id)))}
                className="cursor-pointer border-b border-line last:border-b-0 hover:bg-surface"
              >
                <td className="whitespace-nowrap px-2 py-3 font-medium text-ink">
                  {b.invoiceNumber}
                </td>
                <td className="whitespace-nowrap px-2 py-3 font-semibold tabular-nums text-ink">
                  {billingAmount(b)}
                </td>
                <td className="px-2 py-3">
                  <BillingStatusBadge status={b.status} />
                </td>
                <td className="whitespace-nowrap px-2 py-3 text-ink-muted">
                  {safeFormatDate(b.registeredAt, '—')}
                </td>
                <td className="px-2 py-3">
                  <div className="flex items-center justify-end gap-2">
                    {payable && (
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={(e) => {
                          e.stopPropagation()
                          onPay(b.id)
                        }}
                      >
                        Pagar
                      </Button>
                    )}
                    <ChevronRight className="h-4 w-4 shrink-0 text-ink-subtle" />
                  </div>
                </td>
              </tr>
            )
          })}
        </tbody>
      </table>
    </div>
  )
}
